/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useCallback, useEffect } from "react";
import ReactFlow, {
  Node,
  Controls,
  Background,
  BackgroundVariant,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import { Box, Typography } from "@mui/material";
import type { ChartNode, ChartEdge } from "../../../types/api-types";
import CustomNode from "./custom-node";
import NodeDetailsPanel from "./node-details-panel";
import useFilteredChartData from "./hooks/use-filtered-chart-data";
import useChartTransforms from "./hooks/use-chart-transforms";
import { CHART_CONFIG } from "./chart-constants";

interface LinkChartGraphProps {
  nodes: ChartNode[];
  edges: ChartEdge[];
}

const nodeTypes = {
  custom: CustomNode,
};

/**
 * Inner component that uses ReactFlow hooks
 */
const LinkChartGraphInner: React.FC<LinkChartGraphProps> = ({
  nodes,
  edges,
}) => {
  const [selectedNode, setSelectedNode] = useState<ChartNode | null>(null);
  const { fitView } = useReactFlow();

  const { filteredNodes, filteredEdges } = useFilteredChartData(nodes, edges);

  const { initialNodes, initialEdges } = useChartTransforms(
    filteredNodes,
    filteredEdges,
  );

  const [flowNodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [flowEdges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);

  // Re-fit view when selected node changes (details panel opens/closes)
  useEffect(() => {
    // Delay to allow grid transition to complete
    const timer = setTimeout(() => {
      fitView({
        padding: CHART_CONFIG.FIT_VIEW_PADDING,
        duration: 300,
      });
    }, 250);

    return () => clearTimeout(timer);
  }, [selectedNode, fitView]);

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      const chartNode = filteredNodes.find((n) => n.id === node.id);
      if (chartNode) {
        setSelectedNode(chartNode);
      }
    },
    [filteredNodes],
  );

  const handleCloseDetails = useCallback(() => {
    setSelectedNode(null);
  }, []);

  if (filteredNodes.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "text.secondary",
        }}
      >
        <Typography>No nodes to display</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: selectedNode ? "380px 1fr" : "1fr",
        height: "100%",
        width: "100%",
        transition: "grid-template-columns 0.2s ease",
      }}
    >
      {selectedNode && (
        <Box
          sx={{
            height: "100%",
            borderRight: 1,
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <NodeDetailsPanel node={selectedNode} onClose={handleCloseDetails} />
        </Box>
      )}

      <Box sx={{ height: "100%", width: "100%" }}>
        <ReactFlow
          nodes={flowNodes}
          edges={flowEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{
            padding: CHART_CONFIG.FIT_VIEW_PADDING,
            includeHiddenNodes: false,
          }}
          minZoom={CHART_CONFIG.MIN_ZOOM}
          maxZoom={CHART_CONFIG.MAX_ZOOM}
          defaultEdgeOptions={{
            type: "smoothstep",
            animated: false,
          }}
          nodesDraggable
          nodesConnectable={false}
          nodesFocusable
          elementsSelectable
          panOnScroll
          zoomOnScroll
          zoomOnDoubleClick={false}
          onNodeClick={handleNodeClick}
        >
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </Box>
    </Box>
  );
};

/**
 * Link chart graph component that visualizes entity relationships
 */
const LinkChartGraph: React.FC<LinkChartGraphProps> = ({ nodes, edges }) => {
  return (
    <ReactFlowProvider>
      <LinkChartGraphInner nodes={nodes} edges={edges} />
    </ReactFlowProvider>
  );
};

export default LinkChartGraph;
